import { useMemo } from "react";
import { Fragment } from "react/jsx-runtime";
import { Chip, Divider } from "@mui/material";
import { AlarmIcon } from "@phosphor-icons/react";
import { useTranslations } from "use-intl";
import { extractAirlineCarriers } from "@/app/lib/utils/extractAirlineCarriers";
import type { FindUserReservationsExternalFlightReservation } from "../lib/types/codegen";
import { FlightInfo } from "./flights/FlightInfo";
import FlightArrow from "./FlightArrow";
import { SingleAvailableFlight } from "./SingleAvailableFlight";
import { parseDuration } from "../lib/utils/duration";
import { splitSegmentsToFlights } from "../lib/utils/splitSegmentsToFlights";

type Props = {
  itinerary: FindUserReservationsExternalFlightReservation;
};

export function FlightReservationSegmentsData({ itinerary }: Props) {
  const t = useTranslations();

  const allAirlines = extractAirlineCarriers(itinerary.segments);

  const { departureFlight, returnFlight } = useMemo(
    () => splitSegmentsToFlights(itinerary.segments),
    [itinerary.segments],
  );

  return (
    <SingleAvailableFlight airlines={allAirlines}>
      {[departureFlight, returnFlight].map((segments, index) => (
        <Fragment key={index}>
          {index > 0 && segments.length > 0 ? (
            <Divider
              sx={{
                my: "calc(var(--spacing)*2)",
                borderColor: "var(--color-stroke-neutral-softer)",
              }}
            />
          ) : null}

          {segments.map((segment) => {
            // does operating carrier differ from marketing carrier
            const operatedBy =
              segment.operatingCarrier &&
              segment.marketingCarrier &&
              segment.operatingCarrier.code !== segment.marketingCarrier.code
                ? segment.operatingCarrier.name || segment.operatingCarrier.code
                : undefined;

            return (
              <Fragment key={segment.flightNumber}>
                {segment.stopTime ? (
                  <Chip
                    icon={<AlarmIcon />}
                    className="mx-auto"
                    color="primary"
                    size="small"
                    label={t("flights.flightStopWithTime", {
                      ...parseDuration(segment.stopTime),
                    })}
                    sx={{
                      my: "calc(var(--spacing)*-2)",
                    }}
                  />
                ) : null}

                <FlightInfo
                  origin={segment.origin}
                  destination={segment.destination}
                >
                  <div className="flex flex-col">
                    <FlightArrow
                      overallTime={
                        segment.flightDuration
                          ? t(
                              "durationValue",
                              parseDuration(segment.flightDuration),
                            )
                          : null
                      }
                    />
                    {operatedBy ? (
                      <div className="text-center text-sm text-text-tertiary">{`${t("flights.flightOperatedBy")} ${operatedBy}`}</div>
                    ) : null}
                  </div>
                </FlightInfo>
              </Fragment>
            );
          })}
        </Fragment>
      ))}
    </SingleAvailableFlight>
  );
}
